import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Smartphone, Download, CheckCircle, ArrowRight } from "lucide-react";
import { useIsMobile } from "@/hooks/useIsMobile";

interface DSOnboardingPageProps {
  onDismiss: () => void;
}

export const DSOnboardingPage: React.FC<DSOnboardingPageProps> = ({ onDismiss }) => {
  const isMobile = useIsMobile();
  const [step, setStep] = useState<"initial" | "download">("initial");

  const appStoreUrl = "https://apps.apple.com/app/corevo-mobile/id6743926976";
  const playStoreUrl = "https://play.google.com/store/apps/details?id=com.bookreen.corevo2";

  const handleHaveApp = () => {
    onDismiss();
  };

  const handleDontHaveApp = () => {
    setStep("download");
  };

  const handleDownloadComplete = () => {
    onDismiss();
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-pink-500/10"></div>
      <div className="absolute top-0 left-0 w-72 h-72 bg-gradient-to-br from-blue-400/20 to-purple-600/20 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-pink-400/20 to-red-600/20 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-4 xl:py-12 min-h-screen flex items-center">
        {step === "initial" ? (
          // Initial step - Ask about app availability
          <div className="w-full max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 shadow-2xl text-center">
              <div className="space-y-8">
                <div className="flex items-center justify-center mb-6">
                  <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4 rounded-2xl">
                    <Smartphone className="h-12 w-12 text-white" />
                  </div>
                </div>

                <h1 className="text-4xl font-bold text-white mb-4">Welcome to Corevo Digital Signage</h1>

                <p className="text-blue-100 text-xl leading-relaxed max-w-2xl mx-auto">
                  To get started, we need to check if you have our mobile app installed on your device.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-2xl mx-auto mt-12">
                  <Button
                    onClick={handleHaveApp}
                    className="py-8 text-lg font-semibold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white border-0 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <CheckCircle className="h-6 w-6 mr-3" />
                    Yes, I have the app
                  </Button>

                  <Button
                    onClick={handleDontHaveApp}
                    className="py-8 text-lg font-semibold bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white border-0 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                  >
                    <Download className="h-6 w-6 mr-3" />
                    No, I need to download it
                  </Button>
                </div>

                <div className="mt-8">
                  <p className="text-blue-200 text-sm">
                    The Corevo Mobile app is required to register and manage your digital signage player.
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // Download step - Show QR codes for app stores
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full">
            {/* Left side - iOS App Store */}
            <div>
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-r from-gray-600 to-gray-800 p-3 rounded-2xl">
                      <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                      </svg>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-white mb-2">Download for iOS</h2>

                  <p className="text-blue-100 text-lg leading-relaxed">
                    Scan this QR code with your iPhone or iPad to download from the App Store
                  </p>

                  <div className="bg-white rounded-2xl p-6 shadow-xl">
                    <QRCodeSVG
                      value={appStoreUrl}
                      size={isMobile ? 200 : 250}
                      level="H"
                      className="w-full h-auto max-w-[250px] mx-auto"
                      imageSettings={{
                        src: "/logo.svg",
                        height: 50,
                        width: 80,
                        excavate: true,
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-center space-x-2 text-blue-200">
                    <Smartphone className="h-5 w-5" />
                    <span className="text-sm">Scan with your iOS device</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side - Google Play Store */}
            <div>
              <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-8 border border-white/20 shadow-2xl">
                <div className="space-y-6">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 p-3 rounded-2xl">
                      <svg className="h-8 w-8 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                      </svg>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-white mb-2">Download for Android</h2>

                  <p className="text-blue-100 text-lg leading-relaxed">
                    Scan this QR code with your Android device to download from Google Play
                  </p>

                  <div className="bg-white rounded-2xl p-6 shadow-xl">
                    <QRCodeSVG
                      value={playStoreUrl}
                      size={isMobile ? 200 : 250}
                      level="H"
                      className="w-full h-auto max-w-[250px] mx-auto"
                      imageSettings={{
                        src: "/logo.svg",
                        height: 50,
                        width: 80,
                        excavate: true,
                      }}
                    />
                  </div>

                  <div className="flex items-center justify-center space-x-2 text-blue-200">
                    <Smartphone className="h-5 w-5" />
                    <span className="text-sm">Scan with your Android device</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Continue button for download step */}
        {step === "download" && (
          <div className="fixed bottom-8 left-1/2 transform -translate-x-1/2">
            <Button
              onClick={handleDownloadComplete}
              className="py-4 px-8 text-lg font-semibold bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
            >
              I've downloaded the app
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
